import type { WorldCupPayload } from '@/api/worldCup';

/*
 * Carrefour Coca-Cola Quick — the design handoff, transcribed.
 *
 * DEV-ONLY (see DashboardConfig.devPreviewPayload): it lets the dashboard be
 * reviewed against the design before the pipeline exists. A deployed build
 * never renders it — showing handoff numbers as live data is exactly what the
 * AH reports' *_EMPTY fallbacks exist to prevent.
 *
 * Every figure below comes from the design; if a number here changes, the
 * design changed. The campaign has NO game mechanic and NO sponsor brands, so
 * games/missions/brand fields carry zeros and empty arrays — the same "widgets
 * disappear on data alone" convention the 50 Ans report uses.
 */

const START = '2026-08-24';
const END = '2026-09-06';

// 14 campaign days, 24.08 → 06.09.
const DAYS = Array.from({ length: 14 }, (_, i) => new Date(Date.parse(`${START}T00:00:00Z`) + i * 86_400_000).toISOString().slice(0, 10));

const fmt = (n: number) => n.toLocaleString('en-US');
const count = (value: number, foot?: string, label?: string) => ({ value, display: fmt(value), foot, label });

// Design series, in thousands per day.
const VISITORS_K = [44.5, 49, 47, 42, 44, 46.5, 44.5, 44, 43.5, 45, 38, 44, 44, 35];
const AWARDS_K = [6.03, 2.64, 2.43, 2.06, 2.04, 2.08, 4.11, 4.99, 4.21, 4.01, 2.7, 2.13, 0.08, 2.8];
const CONSENTS_K = [4.55, 4.05, 3.9, 2.7, 3.35, 3.3, 3.3, 3.2, 3.1, 3.8, 2.5, 3.3, 3.2, 2.75];

const series = (values: number[]) => DAYS.map((date, i) => ({ date, value: Math.round(values[i] * 1000) }));

// "Distinct users per consent type" — the same consent taxonomy the 50 Ans
// report ships (this is the France stack), sorted desc as the contract requires.
const CONSENT_TYPES: ReadonlyArray<[string, number]> = [
  ['contact_you', 14_865],
  ['messenger', 6_773],
  ['whatsapp', 6_299],
  ['instagram', 4_971],
  ['dv1', 3_502],
  ['sms', 3_170],
  ['kyc', 2_951],
  ['email', 2_727],
  ['dv2', 1_736],
];

const CONSENT_LABELS: Record<string, string> = {
  contact_you: 'Contact you',
  dv1: 'DV1',
  dv2: 'DV2',
  email: 'Email',
  instagram: 'Instagram',
  kyc: 'KYC',
  messenger: 'Messenger',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
};

export const COCA_COLA_QUICK_PREVIEW: WorldCupPayload = {
  campaign: {
    id: 'carrefour-coca-cola-quick',
    title: 'Carrefour Coca-Cola Quick',
    currency: 'EUR',
    locale: 'fr-FR',
    date_range: { start: START, end: END, weeks: 2 },
    weeks: [
      { iso_week: 35, label: 'Week 35', start: START, end: '2026-08-30' },
      { iso_week: 36, label: 'Week 36', start: '2026-08-31', end: END },
    ],
    // No sponsor brands in this campaign — every brand widget drops out.
    brands: [],
  },
  sections: {
    overview: {
      business_kpis: {
        users_traffic: count(192_508, 'whole campaign'),
        // Headline for the Consents tab.
        consents_total: count(38_891),
      },
      engagement_kpis: {
        unique_optins: count(180_794, 'all channels'),
        unique_visitors: count(192_508, 'whole campaign'),
        avg_daily_visitors: count(43_643, '14-day average', 'Avg daily unique visitors'),
        // No game mechanic: hidden by config, zero in the data.
        games_played: count(0),
        missions_completed: count(0),
        rewards_awarded: count(42_318, 'of 50,000 available', 'Menus earned'),
        rewards_claimed: count(11_667, 'redeemed by users', 'Menus claimed'),
      },
    },
    engagement: {
      kpis: {
        unique_optins: count(180_794, 'all channels'),
        unique_visitors: count(192_508, 'whole campaign'),
      },
      daily_unique_visitors: { label: 'Unique visitors / day', series: series(VISITORS_K) },
      funnel: [],
    },
    // Required by the contract but empty here: no games, no missions, no
    // brands. The tab itself is dropped by config (hide.gamesTab).
    games_missions: {
      kpis: { total_games: count(0), avg_games_per_user: count(0), missions_done: count(0) },
      games: [],
      games_by_brand: [],
    },
    rewards_coupons: {
      // No coupons in this campaign — the design's tab and eyebrow read
      // "Rewards", and the section is "Menu distribution".
      title: 'Menu distribution',
      tag: 'rewards',
      kpis: {
        rewards_awarded: count(42_318, 'of 50,000 pool', 'Menus awarded'),
        rewards_claimed: count(11_667, undefined, 'Menus claimed'),
        coupons_remaining: count(7_682, 'still available to win', 'Menus remaining'),
        contact_you_leads: count(14_865),
      },
      top_prizes: [],
      rewards_by_day: { label: 'Menus awarded per day', series: series(AWARDS_K) },
    },
    consents: {
      by_type: CONSENT_TYPES.map(([type, n]) => ({ type, count: n, label: CONSENT_LABELS[type] })),
      by_day: { label: 'Consents per day', series: series(CONSENTS_K) },
    },
  },
};
